const CURRENCY_SYMBOLS = {
  USD: "$",
  THB: "฿",
  GBP: "£",
  EUR: "€",
  AUD: "A$",
  SGD: "S$",
  MYR: "RM",
  INR: "₹",
  JPY: "¥",
  KRW: "₩",
  BRL: "R$",
  CAD: "C$",
  HKD: "HKD ",
  MXN: "MX$",
  TRY: "₺",
  ZAR: "R",
  PHP: "₱",
  IDR: "Rp",
  AED: "د.إ",
  SAR: "﷼",
  EGP: "E£",
  QAR: "﷼",
};

const CURRENCY_MATCHERS = [
  { regex: /(\bIDR\b|Rp(?=\s*\d))/i, code: "IDR" },
  { regex: /(\bSGD\b|S\$)/i, code: "SGD" },
  { regex: /(\bAUD\b|A\$)/i, code: "AUD" },
  { regex: /(\bCAD\b|C\$)/i, code: "CAD" },
  { regex: /(\bHKD\b|HK\$)/i, code: "HKD" },
  { regex: /(\bNZD\b|NZ\$)/i, code: "NZD" },
  { regex: /(\bUSD\b|US\$|\$(?=\s*\d))/i, code: "USD" },
  { regex: /(\bTHB\b|฿)/i, code: "THB" },
  { regex: /(\bJPY\b|¥)/i, code: "JPY" },
  { regex: /(\bKRW\b|₩)/i, code: "KRW" },
  { regex: /(\bMYR\b|RM(?=\s*\d))/i, code: "MYR" },
  { regex: /(\bPHP\b|₱)/i, code: "PHP" },
  { regex: /(\bTWD\b|NT\$)/i, code: "TWD" },
  { regex: /(\bVND\b|₫)/i, code: "VND" },
  { regex: /(\bGBP\b|£)/i, code: "GBP" },
  { regex: /(\bEUR\b|€)/i, code: "EUR" },
];

export const getCleengCurrencyCode = (cleengPrice) => (
  String((cleengPrice && cleengPrice.currency) || "").toUpperCase()
);

export const isDynamicCleengPricing = (cleengPrice) => {
  const currencyCode = getCleengCurrencyCode(cleengPrice);
  return !!cleengPrice && !!currencyCode && currencyCode !== "USD";
};

export const getCleengCurrencySymbol = (cleengPrice) => {
  const currency = getCleengCurrencyCode(cleengPrice);
  return CURRENCY_SYMBOLS[currency] || `${currency} `;
};

export const getCurrencyMarker = (text) => {
  if (!text) {
    return null;
  }

  const normalizedText = String(text).trim();
  const matchedCurrency = CURRENCY_MATCHERS.find(({ regex }) => regex.test(normalizedText));
  return matchedCurrency ? matchedCurrency.code : null;
};

const parseAmountText = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const text = value.trim();
  if (!text) {
    return null;
  }

  let numericText = text.replace(/[^0-9,.-]/g, "");
  if (!numericText) {
    return null;
  }

  const hasComma = numericText.indexOf(",") >= 0;
  const hasDot = numericText.indexOf(".") >= 0;

  if (hasComma && hasDot) {
    numericText = numericText.replace(/,/g, "");
  } else if (hasComma) {
    const commaParts = numericText.split(",");
    const lastPart = commaParts[commaParts.length - 1];
    if (commaParts.length === 2 && lastPart.length > 0 && lastPart.length <= 2) {
      numericText = numericText.replace(",", ".");
    } else {
      numericText = numericText.replace(/,/g, "");
    }
  } else if (hasDot) {
    const dotParts = numericText.split(".");
    const thousandGroups = dotParts.slice(1);
    // IDR uses . as thousands (Rp145.280). Do not parse that as 145.28.
    if (thousandGroups.length > 0 && thousandGroups.every((part) => part.length === 3)) {
      numericText = dotParts.join("");
    }
  }

  const parsedAmount = Number(numericText);
  return Number.isFinite(parsedAmount) ? parsedAmount : null;
};

export const normalizeCleengAmount = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    return parseAmountText(value);
  }

  return null;
};

export const getCleengFirstAmount = (cleengPrice, fieldNames) => {
  if (!cleengPrice) {
    return null;
  }

  for (let index = 0; index < fieldNames.length; index += 1) {
    const amount = normalizeCleengAmount(cleengPrice[fieldNames[index]]);
    if (Number.isFinite(amount)) {
      return amount;
    }
  }

  return null;
};

export const shouldUseCleengOfferTotalAsCurrent = (cleengPrice) => {
  if (!cleengPrice || isDynamicCleengPricing(cleengPrice)) {
    return false;
  }

  const offerAmount = getCleengFirstAmount(cleengPrice, [
    "offer_price",
    "offer_price_text",
  ]);
  const totalAmount = getCleengFirstAmount(cleengPrice, [
    "total_price",
    "total_price_text",
    "total_price_excl_tax",
    "total_price_excl_tax_text",
  ]);
  const configuredCurrentAmount = getCleengFirstAmount(cleengPrice, [
    "discounted_price",
    "discounted_price_text",
    "main_price",
    "main_price_text",
    "discounted_price_excl_tax",
    "discounted_price_excl_tax_text",
    "main_price_excl_tax",
    "main_price_excl_tax_text",
  ]);

  return Number.isFinite(offerAmount)
    && Number.isFinite(totalAmount)
    && Math.abs(offerAmount - totalAmount) < 0.01
    && (
      !Number.isFinite(configuredCurrentAmount)
      || totalAmount < configuredCurrentAmount
    );
};

const getCurrentAmountFields = (cleengPrice, discounted = true) => {
  const dynamic = isDynamicCleengPricing(cleengPrice);

  if (dynamic) {
    return discounted
      ? [
        "discounted_price_excl_tax",
        "main_price_excl_tax",
        "total_price_excl_tax",
        "discounted_price",
        "main_price",
        "total_price",
        "discounted_price_excl_tax_text",
        "main_price_excl_tax_text",
        "total_price_excl_tax_text",
        "discounted_price_text",
        "main_price_text",
        "total_price_text",
      ]
      : [
        "main_price_excl_tax",
        "total_price_excl_tax",
        "main_price",
        "total_price",
        "main_price_excl_tax_text",
        "total_price_excl_tax_text",
        "main_price_text",
        "total_price_text",
      ];
  }

  if (shouldUseCleengOfferTotalAsCurrent(cleengPrice)) {
    return discounted
      ? [
        "total_price",
        "total_price_excl_tax",
        "offer_price",
        "total_price_text",
        "total_price_excl_tax_text",
        "offer_price_text",
        "discounted_price",
        "main_price",
        "discounted_price_text",
        "main_price_text",
      ]
      : [
        "total_price",
        "total_price_excl_tax",
        "offer_price",
        "total_price_text",
        "total_price_excl_tax_text",
        "offer_price_text",
        "main_price",
        "main_price_text",
      ];
  }

  return discounted
    ? [
      "discounted_price",
      "main_price",
      "total_price",
      "discounted_price_text",
      "main_price_text",
      "total_price_text",
    ]
    : [
      "main_price",
      "total_price",
      "main_price_text",
      "total_price_text",
    ];
};

export const getCleengCurrentAmount = (cleengPrice, { discounted = true } = {}) => (
  getCleengFirstAmount(cleengPrice, getCurrentAmountFields(cleengPrice, discounted))
);

export const getCleengRetailAmount = (cleengPrice) => {
  if (!cleengPrice) {
    return null;
  }

  const currency = getCleengCurrencyCode(cleengPrice);

  // The discount badge must match the visible strikethrough price.
  // For styled Cleeng cards that strikethrough is retail_price_converted_text,
  // so use it before falling back to Cleeng retail/total fields.
  const dynamic = isDynamicCleengPricing(cleengPrice);

  const convertedRetailText = cleengPrice.retail_price_converted_text;
  const convertedRetailTextCurrency = getCurrencyMarker(convertedRetailText);
  if (!dynamic || !convertedRetailTextCurrency || convertedRetailTextCurrency === currency) {
    const convertedRetailTextAmount = normalizeCleengAmount(convertedRetailText);
    if (Number.isFinite(convertedRetailTextAmount)) {
      return convertedRetailTextAmount;
    }
  }

  const convertedRetail = getCleengFirstAmount(cleengPrice, [
    "retail_price_converted",
    "retail_price_converted_excl_tax",
  ]);

  if (Number.isFinite(convertedRetail)) {
    return convertedRetail;
  }

  const retailText = cleengPrice.retail_price_text;
  const retailTextCurrency = getCurrencyMarker(retailText);
  if (!dynamic || !retailTextCurrency || retailTextCurrency === currency) {
    const retailTextAmount = normalizeCleengAmount(retailText);
    if (Number.isFinite(retailTextAmount)) {
      return retailTextAmount;
    }
  }

  const totalText = cleengPrice.total_price_text;
  const totalTextCurrency = getCurrencyMarker(totalText);
  if (!dynamic || !totalTextCurrency || totalTextCurrency === currency) {
    const totalTextAmount = normalizeCleengAmount(totalText);
    const currentAmount = getCleengFirstAmount(cleengPrice, getCurrentAmountFields(cleengPrice, true));
    if (Number.isFinite(totalTextAmount)
      && Number.isFinite(currentAmount)
      && totalTextAmount > currentAmount) {
      return totalTextAmount;
    }
  }

  const retailAmount = normalizeCleengAmount(cleengPrice.retail_price);
  if (Number.isFinite(retailAmount)) {
    return retailAmount;
  }

  return null;
};

/**
 * For dynamic (non-USD) Cleeng pricing, strike/badge only when converted retail
 * beats the inclusive charged total by floor >= 1%. Suppresses tax/FX noise
 * (JP 1-month full-retail: ¥4025 vs ¥3995 charge must not show 9% OFF).
 * Non-dynamic USD keeps existing retail math unchanged.
 */
export const hasMeaningfulCleengRetailDiscount = (cleengPrice) => {
  if (!cleengPrice || !isDynamicCleengPricing(cleengPrice)) {
    return true;
  }

  const convertedRetail = getCleengFirstAmount(cleengPrice, [
    "retail_price_converted",
    "retail_price_converted_text",
  ]);

  // No converted retail → allow coupon-style retail_price_text path.
  if (!Number.isFinite(convertedRetail) || convertedRetail <= 0) {
    return true;
  }

  // Inclusive current-cycle / charged total only (not excl-tax).
  // Gate the floor-% check only when this evidence exists; excl-only card
  // fixtures (retail_price_converted + main/total_price_excl_tax) must keep
  // legacy strike/badge behavior instead of fail-closed suppression.
  const chargedIncl = getCleengFirstAmount(cleengPrice, [
    "discounted_price",
    "main_price",
    "total_price",
    "discounted_price_text",
    "main_price_text",
    "total_price_text",
  ]);

  if (!Number.isFinite(chargedIncl) || chargedIncl <= 0) {
    return true;
  }

  return Math.floor(((convertedRetail - chargedIncl) / convertedRetail) * 100) >= 1;
};

export const calculateCleengDiscountPercentage = (cleengPrice) => {
  if (isDynamicCleengPricing(cleengPrice) && !hasMeaningfulCleengRetailDiscount(cleengPrice)) {
    return null;
  }

  const retailAmount = getCleengRetailAmount(cleengPrice);
  const currentAmount = getCleengCurrentAmount(cleengPrice);

  if (
    !Number.isFinite(retailAmount)
    || !Number.isFinite(currentAmount)
    || retailAmount <= 0
    || currentAmount <= 0
    || retailAmount <= currentAmount
  ) {
    const backendPercentage = Number(cleengPrice && cleengPrice.discount_percentage_from_retail);
    return Number.isFinite(backendPercentage) && backendPercentage >= 0
      ? backendPercentage
      : null;
  }

  return Number((((retailAmount - currentAmount) / retailAmount) * 100).toFixed(1));
};

export const formatCleengDiscountPercentage = (percentage) => {
  const numericPercentage = Number(percentage);
  if (!Number.isFinite(numericPercentage) || numericPercentage <= 0) {
    return null;
  }

  const displayPercentage = Math.floor(numericPercentage);
  return displayPercentage > 0 ? displayPercentage : null;
};

export const formatCleengAmount = (cleengPrice, amount) => {
  if (!Number.isFinite(amount)) {
    return null;
  }

  const currency = getCleengCurrencyCode(cleengPrice);
  if (currency === "IDR") {
    const negative = amount < 0;
    const wholeDigits = String(Math.trunc(Math.abs(amount)));
    const grouped = wholeDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${negative ? "-" : ""}Rp${grouped}`;
  }

  const formatted = Number.isInteger(amount)
    ? amount.toLocaleString()
    : amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  return `${getCleengCurrencySymbol(cleengPrice)}${formatted}`;
};

export const formatCleengMonthlyPrice = (
  cleengPrice,
  {
    months = 1,
    discounted = true,
    divideByBogo = false,
    excludeTax = false,
  } = {},
) => {
  const exclTaxPrice = excludeTax && isDynamicCleengPricing(cleengPrice)
    ? getCleengPackageExclTaxAmount(cleengPrice)
    : null;
  const totalPrice = Number.isFinite(exclTaxPrice)
    ? exclTaxPrice
    : getCleengCurrentAmount(cleengPrice, { discounted });
  const monthCount = Number(months) || 1;

  if (!Number.isFinite(totalPrice) || totalPrice <= 0 || monthCount <= 0) {
    return null;
  }

  const monthlyPrice = totalPrice / monthCount;
  const displayedPrice = divideByBogo ? monthlyPrice / 2 : monthlyPrice;
  return formatCleengAmount(cleengPrice, displayedPrice);
};

export const getCleengPackageExclTaxAmount = (cleengPrice) => (
  getCleengFirstAmount(cleengPrice, [
    "discounted_price_excl_tax",
    "main_price_excl_tax",
    "total_price_excl_tax",
    "discounted_price_excl_tax_text",
    "main_price_excl_tax_text",
    "total_price_excl_tax_text",
  ])
);

export const getCleengPackageExclTaxPriceText = (cleengPrice) => {
  if (!cleengPrice) {
    return null;
  }

  if (!isDynamicCleengPricing(cleengPrice)) {
    return getCleengCurrentPriceText(cleengPrice);
  }

  const textFields = [
    "discounted_price_excl_tax_text",
    "main_price_excl_tax_text",
    "total_price_excl_tax_text",
  ];

  for (let index = 0; index < textFields.length; index += 1) {
    const value = cleengPrice[textFields[index]];
    if (value) {
      return value;
    }
  }

  return formatCleengAmount(cleengPrice, getCleengPackageExclTaxAmount(cleengPrice));
};

export const getCleengCurrentPriceText = (cleengPrice, { discounted = true } = {}) => {
  if (!cleengPrice) {
    return null;
  }

  const dynamic = isDynamicCleengPricing(cleengPrice);
  const shouldPreferOfferTotal = shouldUseCleengOfferTotalAsCurrent(cleengPrice);
  const textFields = dynamic
    ? (
      discounted
        ? [
          "discounted_price_text",
          "main_price_text",
          "total_price_text",
          "discounted_price_excl_tax_text",
          "main_price_excl_tax_text",
          "total_price_excl_tax_text",
        ]
        // Trello 407 (26 AUG 26): excl-tax first, matching getCurrentAmountFields'
        // discounted:false order and the checkout resolver (getCleengPackageExclTaxPriceText).
        // Tax-inclusive first here made the limited-collab card show a JPY figure 1.10x
        // the checkout price (10% consumption tax) while the amount stayed correct.
        : [
          "main_price_excl_tax_text",
          "total_price_excl_tax_text",
          "main_price_text",
          "total_price_text",
        ]
    )
    : (
      shouldPreferOfferTotal
        ? [
          "total_price_text",
          "total_price_excl_tax_text",
          "offer_price_text",
          "discounted_price_text",
          "main_price_text",
        ]
        : discounted
        ? ["discounted_price_text", "main_price_text", "total_price_text"]
        : ["main_price_text", "total_price_text"]
    );

  for (let index = 0; index < textFields.length; index += 1) {
    const value = cleengPrice[textFields[index]];
    if (value) {
      return value;
    }
  }

  return formatCleengAmount(cleengPrice, getCleengCurrentAmount(cleengPrice, { discounted }));
};

export const getCleengRetailPriceText = (cleengPrice, currentPriceText = null) => {
  if (!cleengPrice) {
    return null;
  }

  // Stale/misleading API retail_price_converted_text + tax/FX noise must not
  // render a strike on full-retail dynamic packages (CLEENG-2 JP 1-month).
  if (isDynamicCleengPricing(cleengPrice) && !hasMeaningfulCleengRetailDiscount(cleengPrice)) {
    return null;
  }

  const currency = getCleengCurrencyCode(cleengPrice);
  const textCandidates = [
    cleengPrice.retail_price_converted_text,
    cleengPrice.retail_price_text,
  ];

  for (let index = 0; index < textCandidates.length; index += 1) {
    const text = textCandidates[index];
    const marker = getCurrencyMarker(text);
    if (
      text
      && (!isDynamicCleengPricing(cleengPrice) || !marker || marker === currency)
      && text !== currentPriceText
    ) {
      return text;
    }
  }

  const retailAmount = getCleengRetailAmount(cleengPrice);
  const currentAmount = normalizeCleengAmount(currentPriceText);
  if (
    Number.isFinite(retailAmount)
    && (!Number.isFinite(currentAmount) || retailAmount !== currentAmount)
  ) {
    return formatCleengAmount(cleengPrice, retailAmount);
  }

  return null;
};
